import React, { FC, useContext, useState, useEffect } from 'react';
import { AnimatePresence, m } from 'framer-motion';
import XCircle from '@components/Icons/XCircle';
import Buttons from '@components/Buttons';
import TeamsContext from '@providers/Teams/TeamsContext';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import ProgressIndicator from '@components/ProgressIndicator';
import ChatDropDown from '@components/Chat/ChatDropDown';
import * as ApiModels from '@typings/api-models';
import OutsideClick from '@components/OutsideClick';
import Calendar from '@components/Icons/Calendar';
import s from './InputComposer.module.scss';
import SearchInput from '../FormFields/SearchInput';

type Props = {
  onAttach: (data: { priceId: string; title: string; }) => void;
};

const AddBooking: FC<Props> = ({ onAttach }) => {
  const { creatorId } = useContext(TeamsContext);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [search, setSearch] = useState('');
  const {
    data: schedulingPrices,
    getData: getSchedulingPrices,
    fetchingState: { isFetching: isFetchingSchedulingPrices },
  } = useNoodleApi(tsClient.prices.getSchedulingPrices);

  useEffect(() => {
    if (creatorId) {
      getSchedulingPrices({ creatorId, page: 1, perPage: 1000 });
    }
  }, [creatorId, getSchedulingPrices]);

  const toggle = (): void => {
    setPopoverOpen(prev => !prev);
  };

  const handleClick = (price: Pick<ApiModels.Price, 'id' | 'priceTitle'>): void => {
    setPopoverOpen(false);
    onAttach({ priceId: price.id, title: price.priceTitle || 'Booking' });
  };

  if (schedulingPrices?.items.length === 0) {
    return null;
  }

  return (
    <>
      <Buttons
        ariaLabel={'Add booking'}
        iconBefore={
          <m.div
            style={{
              alignItems: 'center',
              display: 'flex',
              height: '16px',
              justifyContent: 'center',
              width: '16px',
              willChange: 'transform',
            }}
            whileTap={{ scale: 0.9 }}
          >
            {popoverOpen
              ? (
                <XCircle weight="fill" color="var(--color-gray-50)" size={16} />
              )
              : (
                <Calendar size={16} weight="fill" color="var(--color-primary)" />
              )}
          </m.div>
        }
        onClick={toggle}
      >
        Bookings
      </Buttons>
      <AnimatePresence>
        {popoverOpen && (
          <OutsideClick onOutsideClick={() => setPopoverOpen(false)} className={s.outsideClick}>
            <ChatDropDown>
              {!schedulingPrices || isFetchingSchedulingPrices
                ? (
                  <ProgressIndicator isCentered />
                )
                : (
                  <>
                    <SearchInput label="Filter by title" placeholder="Type a title..." setSearch={setSearch} className={s.search} />
                    {schedulingPrices.items
                      .filter(price => price.priceTitle?.toLowerCase().includes(search.toLowerCase()))
                      .map(price => (
                        <Buttons className={s.documentRequestButton} key={price.id} isWrapper isFlat onClick={() => handleClick(price)}>
                          <Calendar size={14} weight="fill" color="var(--color-primary)" />
                          <p>
                            {price.priceTitle}
                          </p>
                        </Buttons>
                      ))}
                  </>
                )}
            </ChatDropDown>
          </OutsideClick>
        )}
      </AnimatePresence>
    </>
  );
};

export default AddBooking;
